var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { ButtonFunction } from '@snack-uikit/button';
import { ChevronLeftSVG, ChevronRightSVG } from '@snack-uikit/icons';
import { extractSupportProps } from '@snack-uikit/utils';
import { SIZE, VARIANT } from '../../constants';
import { PaginationContext } from '../../contexts';
import { getPaginationEntries, PaginationEntryKind } from '../../utils';
import { PageMoreButton } from '../PageMoreButton';
import { PageNumberButton } from '../PageNumberButton';
import styles from './styles.module.css';
const FIRST_PAGE = 1;
const ARROW_STEP = 1;
const MAX_LENGTH = 7;
export function Pagination(_a) {
    var { total, page, onChange, hrefFormatter, className, variant = VARIANT.Button, size = SIZE.S, maxLength = MAX_LENGTH } = _a, rest = __rest(_a, ["total", "page", "onChange", "hrefFormatter", "className", "variant", "size", "maxLength"]);
    const entries = getPaginationEntries({
        firstPage: FIRST_PAGE,
        lastPage: total,
        currentPage: page,
        maxLength,
    });
    const buttonRefs = useRef([]);
    const [buttonToFocus, setButtonToFocus] = useState(-1);
    useEffect(() => {
        var _a;
        (_a = buttonRefs.current[buttonToFocus]) === null || _a === void 0 ? void 0 : _a.focus();
    }, [buttonToFocus]);
    const handlePreviousPageButtonClick = () => {
        const newPage = page - ARROW_STEP;
        onChange(newPage);
        if (newPage === FIRST_PAGE) {
            setButtonToFocus(FIRST_PAGE);
        }
    };
    const handleNextPageButtonClick = () => {
        const newPage = page + ARROW_STEP;
        onChange(newPage);
        if (newPage === total) {
            setButtonToFocus(total);
        }
    };
    const handleMoreButtonClick = (start, end) => {
        const page = Math.floor((start + end) / 2);
        onChange(page);
        setButtonToFocus(page);
    };
    const renderEntry = (entry) => {
        if (entry.kind === PaginationEntryKind.Page) {
            return (_jsx("li", { children: _jsx(PageNumberButton, { page: entry.page, activated: entry.page === page, onClick: onChange, setButtonRef: (el) => {
                        buttonRefs.current[entry.page] = el;
                    }, hrefFormatter: hrefFormatter }) }, entry.page));
        }
        if (entry.kind === PaginationEntryKind.Break) {
            return (_jsx("li", { children: _jsx(PageMoreButton, { start: entry.start, end: entry.end, onClick: handleMoreButtonClick }) }, `${entry.start}-${entry.end}`));
        }
    };
    return (_jsx(PaginationContext.Provider, { value: { size, variant }, children: _jsx("nav", Object.assign({ className: className }, extractSupportProps(rest), { children: _jsxs("ul", { className: styles.pagination, children: [_jsx("li", { children: _jsx(ButtonFunction, { icon: _jsx(ChevronLeftSVG, {}), onClick: handlePreviousPageButtonClick, disabled: page === FIRST_PAGE, "data-test-id": 'page-prev-button', size: size }) }), entries.map(renderEntry), _jsx("li", { children: _jsx(ButtonFunction, { icon: _jsx(ChevronRightSVG, {}), onClick: handleNextPageButtonClick, disabled: page === total, "data-test-id": 'page-next-button', size: size }) })] }) })) }));
}
